import {Button, Checkbox, Modal, Stack, TextInput} from "@mantine/core";
import {useForm} from "@mantine/form";
import FailureReportApi, {UpdateFailureReport} from "../../../api/failure/report/FailureReportApi";
import {showNotification} from "@mantine/notifications";
import {DateTimePicker} from "@mantine/dates";
import FailureReport from "../../../api/failure/report/FailureReport";
import {useEffect} from "react";

type Props = {
    failureReport: FailureReport | undefined;
    onClose: () => void;
    closeAndUpdate: () => void;
}

const parse = (dt: string | undefined | null) => {
    if (!dt) {
        return undefined;
    }

    const [datePart, timePart] = dt.split(' ');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    return new Date(year, month - 1, day, hours, minutes);
};

const format = (dateString: string | Date) => {
    if (dateString === null || dateString === undefined || dateString === "") {
        return undefined;
    }

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Adding 1 because getMonth() returns zero-based month
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);

    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
}

function UpdateFailureReportDialog(props: Props) {
    const form = useForm<any>({
        mode: 'uncontrolled',
        initialValues: {
            title: '',
            unit: '',
            dateTime: '',
            deviceNumber: '',
            storeVaccinesFrom: "",
            storeVaccinesPlace: "",
            storeVaccinesTo: "",
            fromDate: "",
            toDate: "",
            isFinished: false
        }
    });

    const onSubmit = (data: any) => {
        const payload: UpdateFailureReport = {
            ...data,

            storeVaccinesTo: format(data.storeVaccinesTo),
            storeVaccinesFrom: format(data.storeVaccinesFrom),
            dateTime: format(data.dateTime),
            fromDate: format(data.fromDate),
            toDate: format(data.toDate),
        }

        if (payload.isFinished === true && (payload.toDate === undefined || payload.toDate === null || payload.toDate === "")) {
            showNotification({
                message: "Nie można zakończyć interwencji nie podając daty zakończenia, proszę ustawić datę zakończenia.",
                color: "red",
            });

            form.setErrors({
                toDate: "Nie można zakończyć interwencji nie podając daty zakończenia, proszę ustawić datę zakończenia."
            });

            return;
        }

        if (props.failureReport === undefined) {
            throw new Error("props.failureReport is undefined, this should not happen");
        }

        FailureReportApi.update(props.failureReport.id, payload)
            .then(props.closeAndUpdate)
            .catch(() => showNotification({color: 'red', message: 'Nie udało się utworzyć raportu'}))
    }

    useEffect(() => {
        form.setValues({
            title: props.failureReport?.title,
            unit: props.failureReport?.unit,
            isFinished: props.failureReport?.isFinished || false,
            dateTime: parse(props.failureReport?.dateTime as any as string),
            fromDate: parse(props.failureReport?.fromDate),
            toDate: parse(props.failureReport?.toDate),
            storeVaccinesFrom: parse(props.failureReport?.storeVaccinesFrom),
            storeVaccinesTo: parse(props.failureReport?.storeVaccinesTo),
            storeVaccinesPlace: props.failureReport?.storeVaccinesPlace
        })
        // eslint-disable-next-line
    }, [props.failureReport]);

    return (
        <Modal
            opened={props.failureReport !== undefined}
            onClose={props.onClose}
            title="Zmień raport z interwencji"
        >
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Stack gap="sm">
                    { props.failureReport?.isFinished === true && (
                        <div style={{ color: "red" }}>
                            Interwencja została zakończona, nie można już jej edytować
                        </div>
                    )}

                    <TextInput
                        label="Tytuł raportu"
                        readOnly={props.failureReport?.isFinished === true}
                        required
                        placeholder="Tytuł raportu z interwencji, widoczny tylko dla administratora"
                        key={form.key('title')}
                        {...form.getInputProps('title')}
                    />

                    <TextInput
                        required
                        readOnly={props.failureReport?.isFinished === true}
                        placeholder="Jednostka"
                        key={form.key('unit')}
                        {...form.getInputProps('unit')}
                    />

                    <DateTimePicker
                        required
                        readOnly={props.failureReport?.isFinished === true}
                        valueFormat="DD-MM-YYYY"
                        label="Data"
                        key={form.key('dateTime')}
                        {...form.getInputProps('dateTime')}
                    />

                    <DateTimePicker
                        clearable
                        valueFormat="DD-MM-YYYY HH:mm"
                        readOnly={props.failureReport?.isFinished === true}
                        label="Przechowywanie szczepionek (Od)"
                        key={form.key('storeVaccinesFrom')}
                        {...form.getInputProps('storeVaccinesFrom')}
                    />

                    <DateTimePicker
                        clearable
                        valueFormat="DD-MM-YYYY HH:mm"
                        readOnly={props.failureReport?.isFinished === true}
                        label="Przechowywanie szczepionek (Do)"
                        key={form.key('storeVaccinesTo')}
                        {...form.getInputProps('storeVaccinesTo')}
                    />

                    <TextInput
                        label="Miejsce przechowywania szczepionek"
                        readOnly={props.failureReport?.isFinished === true}
                        key={form.key('storeVaccinesPlace')}
                        {...form.getInputProps('storeVaccinesPlace')}
                    />

                    <DateTimePicker
                        clearable
                        readOnly={props.failureReport?.isFinished === true}
                        valueFormat="DD-MM-YYYY HH:mm"
                        label="Czas trwania (Od)"
                        key={form.key('fromDate')}
                        {...form.getInputProps('fromDate')}
                    />

                    <DateTimePicker
                        clearable
                        readOnly={props.failureReport?.isFinished === true}
                        valueFormat="DD-MM-YYYY HH:mm"
                        label="Czas trwania (Do)"
                        key={form.key('toDate')}
                        {...form.getInputProps('toDate')}
                    />

                    <Checkbox
                        disabled={props.failureReport?.isFinished === true}
                        defaultChecked={props.failureReport?.isFinished === true}
                        label="Zakończona"
                        description='Spowoduje to wygenerowanie raportu z interwencji w zakładce "Raporty". Po zakończeniu interwencji nie można jej ponownie otworzyć'
                        key={form.key('isFinished')}
                        {...form.getInputProps('isFinished')}
                    />

                    <Button
                        fullWidth
                        disabled={props.failureReport?.isFinished === true}
                        type="submit"
                    >
                        Zapisz
                    </Button>
                </Stack>
            </form>
        </Modal>
    );
}

export default UpdateFailureReportDialog;